import _ from "lodash";
import { action, flow, observable, ObservableMap } from "mobx";
import { observer } from "mobx-react";
import { useEffect, useMemo, useState } from "react";
import { CvpQueryClient, useCvpQueryClient } from "../api/queryClient";
import { GetCvPortalTranslationOverridesResponse } from "../api/settings/getCvPortalTranslationOverrides";
import { CvpLocale } from "../constants/CvpLocale";
import { getSourceMap } from "./getSourceMap";
import { getTranslationMap } from "./getTranslationMap";
import { ITextAndTranslation } from "./ITextAndTranslation";
import { loc } from "./loc";
import { LocContext } from "./LocContext";
import { cvpTextPreview } from "./textPreview";
import { textPreviewMode } from "./textPreviewMode";
import {
  getPreviewTranslationOverrides,
  previewTranslationOverridesSetIndicator,
  useRefetchTranslationOverridesAfterAdminSave,
} from "./previewTranslationOverrides";
import { split, SplitReplacement } from "./split";
import { splitStr, SplitStrReplacement } from "./splitStr";
import {
  translationOverrides,
  translationOverridesSetIndicator,
  useTranslationOverrides,
} from "./translationOverrides";
import { useEvaluatedLocale } from "./useEvaluatedLocale";
import { guid } from "../utils/util";

interface IGetTranslationsForLocaleResponse {
  locale: CvpLocale;
  stringContext: LocContext;
  items: ITextAndTranslation[];
}
async function getTranslationsForLocale(
  stringContext: LocContext,
  locale: CvpLocale,
  cvpQueryClient: CvpQueryClient
): Promise<IGetTranslationsForLocaleResponse> {
  const translationsPromise =
    locale !== CvpLocale.nb ? getTranslationMap(stringContext, locale) : null;

  const sourcesPromise = getSourceMap(stringContext, CvpLocale.nb);

  let [translations, sources] = await Promise.all([
    translationsPromise,
    sourcesPromise,
  ]);

  if (locale === CvpLocale.nb) {
    translations = sources;
  }

  // If we are missing translations, we will fetch the english translations too
  let englishTranslations: { [frontendId: string]: any } = {};

  let isMissingTranslations = Object.keys(sources).some(
    (key) => !translations[key]
  );

  if (isMissingTranslations && locale !== CvpLocale.en) {
    englishTranslations = await getTranslationMap(stringContext, CvpLocale.en);
  }
  // --------------------------------------------

  const items: ITextAndTranslation[] = [];
  Object.keys(sources).forEach((key) => {
    items.push({
      frontendId: key,
      text: sources[key],
      translated: translations[key] ?? "",
      englishTranslation: englishTranslations[key] ?? "",
      isOverride: false,
    });
  });

  return {
    locale,
    stringContext,
    items,
  };
}

// Handles fetching of translations and puts them in the global mobx store locStoreInstancesObservable
// we could do this without mobx but it's a lot of work to reimplement the same reactive behavior
export const TranslationProvider = observer(() => {
  const evaluatedLocale = useEvaluatedLocale();
  const instances = Object.values(locStoreInstancesObservable.get());

  const stringContexts = instances.map((instance) => instance.stringContext);
  const cvpQueryClient = useCvpQueryClient();

  useTranslationOverrides();
  useRefetchTranslationOverridesAfterAdminSave();

  useEffect(() => {
    flow(function* () {
      if (!evaluatedLocale) return;
      const _locale = evaluatedLocale as CvpLocale;

      const promises: Promise<IGetTranslationsForLocaleResponse>[] = [];
      const instancesThatNeedTranslations = instances.filter((i) => {
        if (i.gotTranslationsForLocale === evaluatedLocale) return false;
        if (i.waitingForTranslationsForLocale === evaluatedLocale) return false;
        return true;
      });

      const stringContextsThatNeedTranslations = _.uniq(
        instancesThatNeedTranslations.map((i) => i.stringContext)
      );

      stringContextsThatNeedTranslations.forEach((stringContext) => {
        promises.push(
          getTranslationsForLocale(stringContext, _locale, cvpQueryClient)
        );
      });

      instancesThatNeedTranslations.forEach((i) => {
        i.waitingForTranslationsForLocale = _locale;
      });

      const translationResponses: IGetTranslationsForLocaleResponse[] =
        yield Promise.all(promises);

      translationResponses.forEach((translationResponse) => {
        const instance = instances.find(
          (i) => i.stringContext === translationResponse.stringContext
        );

        if (!instance) return;

        instance.translations.replace(
          translationResponse.items.reduce((acc, item) => {
            acc[item.frontendId] = item;
            return acc;
          }, {} as { [frontendId: string]: ITextAndTranslation })
        );

        instance.translationsSetIndicator = guid();
        instance.gotTranslationsForLocale = translationResponse.locale;
        instance.waitingForTranslationsForLocale = null;
        instance.onLocaleLoadedCallbacks.forEach((cb) =>
          cb(translationResponse.locale)
        );
      });
    })();
  }, [stringContexts.join(","), evaluatedLocale]);

  return null;
});

interface LocStoreInstances {
  [stringContext: string]: LocStoreInstance;
}
const locStoreInstances: LocStoreInstances = {};
const locStoreInstancesObservable = observable.box<LocStoreInstances>({});

/**
 * Get object used for translating text.
 * Use useLocStore instead when possible
 */
export const getLocStore = (stringContext: LocContext): LocStoreInstance => {
  if (!stringContext) throw new Error("getLocStore requires stringContext");

  let instance = locStoreInstances[stringContext];

  if (!instance) {
    instance = observable({
      _overrides: null,
      get overrides() {
        if (!translationOverrides.get()) return null;

        const items = [...translationOverrides.get().items];

        const previews = getPreviewTranslationOverrides();

        for (const o of previews) {
          const existing = items.find(
            (i) => i.idText === o.idText && i.locale === o.locale
          );
          if (existing) {
            items.splice(items.indexOf(existing), 1, o);
          } else {
            items.push(o);
          }
        }

        return {
          ...translationOverrides.get(),
          items,
        };
      },

      get changeIndicator() {
        const a = instance.translationsSetIndicator;
        const b = translationOverridesSetIndicator.get();
        const c = previewTranslationOverridesSetIndicator.get();

        return `${a}_${b}_${c}`;
      },

      translations: observable.map<string, ITextAndTranslation>(),
      translationsSetIndicator: null,
      onLocaleLoadedCallbacks: [],
      onLocaleLoaded: (cb: (locale: CvpLocale) => void) => {
        instance.onLocaleLoadedCallbacks.push(cb);

        // Return a function that will remove the callback from the list
        return () => {
          instance.onLocaleLoadedCallbacks =
            instance.onLocaleLoadedCallbacks.filter((c) => c !== cb);
        };
      },
      getTranslation: (
        frontendId: string,
        _text: string,
        _stringContext: string
      ) => {
        const override = instance.overrides?.items.find(
          (override) =>
            override.idText === frontendId &&
            override.locale === instance.gotTranslationsForLocale
        );

        const overrideEn = instance.overrides?.items.find(
          (o) => o.idText === frontendId && o.locale === CvpLocale.en
        );

        const tt = instance.translations.get(frontendId);
        if (!tt) {
          return null;
        }

        let translated = override?.translation ?? tt?.translated;
        let englishTranslation =
          overrideEn?.translation ?? tt?.englishTranslation;

        return {
          text: tt.text,
          translated,
          englishTranslation,
          frontendId,
          isOverride: !!override,
        } as ITextAndTranslation;
      },
      gotTranslationsForLocale: null,
      waitingForTranslationsForLocale: null,
      id: (id: string) => getLocStoreId(id, stringContext, instance),
      stringContext,
    });

    locStoreInstances[stringContext] = instance;

    setTimeout(
      action(() => {
        locStoreInstancesObservable.set(locStoreInstances);
      })
    );
  }

  return locStoreInstances[stringContext];
};

/**
 * Get object used for translating text
 */
export const useLocStore = (locContext: LocContext): LocStoreInstance => {
  const instance = useMemo(() => getLocStore(locContext), []);

  // trigger rerender when translations are loaded
  const [_, setRerender] = useState("");

  useEffect(() => {
    return instance.onLocaleLoaded((locale) => {
      setRerender((r) => locale);
    });
  }, []);

  return instance;
};

export interface LocStoreInstance {
  translations: ObservableMap<string, ITextAndTranslation>;
  translationsSetIndicator: string;
  getTranslation: (
    frontendId: string,
    text: string,
    stringContext: string
  ) => ITextAndTranslation;
  stringContext: LocContext;
  gotTranslationsForLocale: CvpLocale;
  waitingForTranslationsForLocale: CvpLocale;
  onLocaleLoaded: (cb: (locale: CvpLocale) => void) => () => void;
  onLocaleLoadedCallbacks: ((locale: CvpLocale) => void)[];
  readonly overrides: GetCvPortalTranslationOverridesResponse;

  /** When translations on this change this string also changes */
  readonly changeIndicator: string;

  id: (id: string) => {
    loc: (s: string, searchWords?: string[]) => any;
    split: (
      s: string,
      replacements: SplitReplacement,
      searchWords?: string[]
    ) => any;
    splitStr: (
      s: string,
      replacements: SplitStrReplacement,
      searchWords?: string[]
    ) => any;
  };
}

const getLocStoreId = (
  id: string,
  stringContext: string,
  locStoreInstance: LocStoreInstance
) => {
  if (textPreviewMode.get()) {
    return {
      loc: (s: string, searchWords?: string[]) => {
        return cvpTextPreview({
          id,
          text: s,
          searchWords,
          locType: "loc",
          stringContext,
          className: "wsHelpText-loc",
          locStoreInstance,
        });
      },
      split: (
        s: string,
        replacements: SplitReplacement,
        searchWords?: string[]
      ) => {
        return cvpTextPreview({
          id,
          text: s,
          replacements,
          searchWords,
          locType: "split",
          stringContext,
          className: "wsHelpText-split",
          locStoreInstance,
        });
      },
      splitStr: (
        s: string,
        replacements: SplitStrReplacement,
        searchWords?: string[]
      ) => {
        /** splitStr should only return a string and therefore cannot have helptext */
        return splitStr(
          s,
          replacements,
          searchWords,
          stringContext,
          id,
          locStoreInstance
        );
      },
    };
  }

  return {
    loc: (s: string, searchWords?: string[]) => {
      return loc(s, searchWords, stringContext, id, locStoreInstance);
    },
    split: (
      s: string,
      replacements: SplitReplacement,
      searchWords?: string[]
    ) => {
      return split(
        s,
        replacements,
        searchWords,
        stringContext,
        id,
        locStoreInstance
      );
    },
    splitStr: (
      s: string,
      replacements: SplitStrReplacement,
      searchWords?: string[]
    ) => {
      return splitStr(
        s,
        replacements,
        searchWords,
        stringContext,
        id,
        locStoreInstance
      );
    },
  };
};
